<template>
    <v-container id="docForm" class="max-width">
        <!-- Page loader while fetching invoice -->
        <v-row v-if="pageLoading" justify="center" align="center" style="min-height: 300px">
            <v-col cols="12" class="text-center">
                <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
                <div class="mt-4 subtitle-1 grey--text">Loading invoice...</div>
            </v-col>
        </v-row>

        <div v-else>
            <v-card>
                <v-toolbar dense flat color="secondary">
                    <v-toolbar-title>Edit Invoice #{{ Invoice.DocNum }}</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" small text @click="goBack">
                        <v-icon left dark>mdi-keyboard-backspace</v-icon>Back
                    </v-btn>
                </v-toolbar>
                <v-progress-linear v-if="loader" indeterminate color="primary"></v-progress-linear>

                <v-card-text>
                    <v-row>

                        <v-col cols="12" md="3">
                            <v-autocomplete autocomplete="off" dense outlined v-model="record.CardCode"
                                :items="customers" label="Select Client" item-text="Name" item-value="id"
                                :search-input.sync="searchCustomers">
                            </v-autocomplete>
                        </v-col>

                        <v-col cols="12" md="3">
                            <v-autocomplete autocomplete="off" dense outlined v-model="record.Currency"
                                :items="currencies" label="Currency" item-text="Name" item-value="id"
                                @change="selectCurrency(record.Currency)">
                            </v-autocomplete>
                        </v-col>

                        <v-col cols="12" md="3">
                            <v-autocomplete autocomplete="off" dense outlined v-model="record.InvoiceType"
                                :items="saleTypes" label="Invoice Type" item-text="Name" item-value="id">
                            </v-autocomplete>
                        </v-col>

                        <v-col cols="12" md="3">
                            <DatePicker @date="setDate" :myDate="date" :title="`Date`" ref="dateRef">
                            </DatePicker>
                        </v-col>

                        <v-col cols="12" md="3">
                            <v-text-field autocomplete="off" dense outlined v-model="record.AwbNo"
                                label="AWB No / LPO No"></v-text-field>
                        </v-col>

                    </v-row>
                </v-card-text>
            </v-card>
            <!-- end of first card -->

            <!-- Line Items -->
            <v-card class="mt-4">
                <v-card-text>
                    <v-row dense id="po">
                        <v-col cols="12">
                            <v-data-table :item-class="getRowClass" light dense :items-per-page="100"
                                :headers="itemHeaders" :items="orderItems" item-key="id" hide-default-footer
                                disable-sort>

                                <template v-slot:item.itemCode="{ item }">
                                    <div style="font-weight: bold; font-size: 14px; width: 100px; padding-left: 10px">
                                        {{ item.itemCode }}
                                    </div>
                                </template>

                                <template v-slot:item.Name="{ item }">
                                    <div style="font-weight: bold; margin-left: 20px; font-size: 14px; width: 300px;">
                                        {{ item.product && item.product.Name }}
                                    </div>
                                </template>

                                <template v-slot:item.UomName="{ item }">
                                    <div>{{ item.UOM && item.UOM.UomName || 'Pcs' }}</div>
                                </template>

                                <template v-slot:item.Quantity="{ item }">
                                    <v-text-field style="padding-right: 10px;" autocomplete="off" type="number"
                                        class="orderInput" @input="recalculateRow(item)" single-line outlined dense
                                        v-model="item.Quantity"></v-text-field>
                                </template>

                                <template v-slot:item.TotalWeight="{ item }">
                                    <div
                                        style="font-weight: bold; font-size: 14px; width: 100px; padding-left: 10px">
                                        {{ item.TotalWeight }}
                                    </div>
                                </template>

                                <template v-slot:item.Price="{ item }">
                                    <v-text-field style="padding-right: 10px;" autocomplete="off" type="number"
                                        class="orderInput" @input="recalculateRow(item)" single-line outlined dense
                                        v-model="item.Price" :prefix="currencySymbol"></v-text-field>
                                </template>

                                <template v-slot:item.LineTotal="{ item }">
                                    <div style="font-weight: bold; font-size: 14px; width: 100px">
                                        {{ currencySymbol }} {{ item.LineTotal | currency }}
                                    </div>
                                </template>

                            </v-data-table>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
            <!-- end of card -->

            <!-- cost variables / summary -->
            <v-row>
                <v-col cols="12" md="6">
                    <v-card class="mt-4">
                        <v-card-text>
                            <v-row>
                                <v-col cols="12">
                                    <span style="font-size: 16px; font-weight: bold">Cost Variables</span>
                                    <v-data-table light dense :items-per-page="20" :headers="costHeaders"
                                        :items="costVariables" item-key="CostId" hide-default-footer disable-sort>

                                        <template v-slot:item.Name="{ item }">
                                            <div
                                                style="font-weight: bold; margin-left: 20px; font-size: 14px; width: 300px;">
                                                {{ item.costVariable && item.costVariable.Name }}
                                            </div>
                                        </template>

                                        <template v-slot:item.Price="{ item }">
                                            <v-text-field style="padding-right: 10px;" autocomplete="off" type="number"
                                                class="orderInput" single-line outlined dense
                                                v-model="item.Price"></v-text-field>
                                        </template>

                                        <template v-slot:item.action="{ item }">
                                            <v-btn @click="removeCostVariable(item)" color="red" icon>
                                                <v-icon>mdi-close</v-icon>
                                            </v-btn>
                                        </template>

                                    </v-data-table>

                                    <v-autocomplete autocomplete="off" class="mt-2" style="width: 400px"
                                        v-model="selectedCostVariable" :items="availableCostVariables"
                                        item-text="Name" item-value="id" label="Add Cost Variable" outlined dense
                                        hide-details @change="addCostVariable">
                                    </v-autocomplete>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-col>

                <!-- right column -->
                <v-col cols="12" md="6">
                    <v-card class="mt-4">
                        <v-card-text>
                            <v-row dense class="pa-1">
                                <v-col cols="6" class="py-2 font-weight-light">
                                    <span style="font-weight: 600;">Total Amount:</span>
                                </v-col>
                                <v-col cols="6" class="py-2">
                                    <span style="font-weight: 600;">{{ currencySymbol }} {{ totalAmount | currency
                                        }}</span>
                                </v-col>

                                <v-col cols="6" class="py-2 font-weight-light">
                                    <span style="font-weight: 600;">Total Amount (Ksh):</span>
                                </v-col>
                                <v-col cols="6" class="py-2">
                                    <span style="font-weight: 600;">KES {{ convertedTotal | currency }}</span>
                                </v-col>

                                <v-col cols="6" class="py-2 font-weight-light">
                                    <span style="font-weight: 600;">Total Cost:</span>
                                </v-col>
                                <v-col cols="6" class="py-2">
                                    <span style="font-weight: 600;">KES {{ totalCostVariables | currency }}</span>
                                </v-col>

                                <v-col cols="6" class="py-2 font-weight-light">
                                    <span style="font-weight: 600;">Margin:</span>
                                </v-col>
                                <v-col cols="6" class="py-2">
                                    <span style="font-weight: 600;">KES {{ (convertedTotal - totalCostVariables) |
                                        currency }}</span>
                                </v-col>

                                <v-col cols="6" class="py-2 font-weight-light">
                                    <span style="font-weight: 600;">Total Freight Cost:</span>
                                </v-col>
                                <v-col cols="6" class="py-2">
                                    <v-text-field autocomplete="off" type="text" hide-details outlined dense
                                        label="Amount" v-model="freightCost"></v-text-field>
                                </v-col>

                                <v-col cols="6" class="py-2 font-weight-light">
                                    <span style="font-weight: 600;">Advance Payment:</span>
                                </v-col>
                                <v-col cols="6">
                                    <v-text-field autocomplete="off" type="text" hide-details outlined dense
                                        label="Amount" v-model="advancePayment"></v-text-field>
                                </v-col>

                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
            <!-- end of cost variables / summary -->

            <v-row class="mt-6">
                <v-divider></v-divider>
            </v-row>

            <v-row class="mb-4 mt-4">
                <v-col cols="12">
                    <v-btn color="primary" class="float-right" @click="saveChanges" :loading="loader"
                        :disabled="loader">
                        <v-icon left>mdi-content-save</v-icon>Update Invoice
                    </v-btn>
                </v-col>
            </v-row>

            <!-- snackbar -->
            <snackbar ref="snackbar"></snackbar>
        </div>
    </v-container>
</template>

<script>
export default {
    name: "EditExportInvoice",
    data: () => ({
        loader: false,
        pageLoading: true,
        Invoice: {},
        record: {},
        date: null,
        currencySymbol: "",
        conversionRate: 1,
        customers: [],
        currencies: [],
        saleTypes: [],
        searchCustomers: null,
        debounceCustomers: null,
        orderItems: [],
        costVariables: [],
        freightCost: 0,
        advancePayment: 0,
        itemHeaders: [
            { text: "Description", value: "Name" },
            { text: "Packsize Per Box", value: "UomName" },
            { text: "HS CODE", value: "itemCode" },
            { text: "No. Of Boxes", value: "Quantity" },
            { text: "Total Weight", value: "TotalWeight" },
            { text: "Price Per Box", value: "Price" },
            { text: "Total Amount", value: "LineTotal" },
        ],
        costHeaders: [
            { text: "Item", value: "Name" },
            { text: "Price", value: "Price" },
            { text: "Action", value: "action" },
        ],
        allCostVariables: [],
        selectedCostVariable: null,
    }),
    computed: {
        totalAmount() {
            if (!this.orderItems || this.orderItems.length === 0) return 0;
            return this.orderItems.reduce((sum, item) => {
                return sum + parseFloat(item.LineTotal || 0);
            }, 0);
        },
        convertedTotal() {
            return this.totalAmount * this.conversionRate;
        },
        totalWeight() {
            if (!this.orderItems || this.orderItems.length === 0) return 0;
            return this.orderItems.reduce((sum, item) => {
                return sum + parseFloat(item.TotalWeight || 0);
            }, 0).toFixed(2);
        },
        totalCostVariables() {
            if (!this.costVariables || this.costVariables.length === 0) return 0;
            return this.costVariables.reduce((sum, item) => {
                return sum + parseFloat(item.Price || 0);
            }, 0);
        },
        availableCostVariables() {
            const usedIds = this.costVariables.map(cv => cv.CostId);
            return this.allCostVariables.filter(cv => !usedIds.includes(cv.id));
        },
    },
    watch: {
        "$route.params.id": {
            handler: "loadInvoice",
            immediate: true,
        },
        searchCustomers(val) {
            if (val && val !== this.record.CardCode) {
                clearTimeout(this.debounceCustomers);
                this.debounceCustomers = setTimeout(() => {
                    this.queryCustomers(val);
                }, 300);
            }
        },
    },
    methods: {
        getRowClass() {
            return "";
        },
        goBack() {
            this.$router.push(`/export-sale-invoices/${this.$route.params.id}`);
        },
        setDate(date) {
            this.date = date;
        },
        selectCurrency(id) {
            const selectedCurrency = this.currencies.find(record => {
                return record.id === id;
            });
            if (selectedCurrency) {
                this.currencySymbol = selectedCurrency.Symbol;
                this.conversionRate = selectedCurrency.ConversionRate;
            } else {
                this.currencySymbol = "";
                this.conversionRate = 1;
            }
        },
        queryCustomers(searchText) {
            const self = this;
            const url = "/customers/search";
            const data = { searchText: searchText };
            this.$store
                .dispatch("post", { url, data })
                .then(res => {
                    if (res.ResultCode == 1200) {
                        self.customers = res.ResponseData;
                    }
                })
                .catch(() => { });
        },
        getCurrencies() {
            const self = this;
            this.$store
                .dispatch("get", `/currencies`)
                .then((res) => {
                    if (res.ResultCode == 1200) {
                        self.currencies = res.ResponseData;
                    }
                })
                .catch(() => { });
        },
        getSaleTypes() {
            const self = this;
            this.$store
                .dispatch("get", `/sale-types`)
                .then((res) => {
                    if (res.ResultCode == 1200) {
                        self.saleTypes = res.ResponseData;
                    }
                })
                .catch(() => { });
        },
        getAllCostVariables() {
            const self = this;
            this.$store
                .dispatch("get", `/cost-variables`)
                .then((res) => {
                    if (res.ResultCode == 1200) {
                        self.allCostVariables = res.ResponseData || [];
                    }
                })
                .catch(() => { });
        },
        addCostVariable(id) {
            const variable = this.allCostVariables.find(cv => cv.id === id);
            if (!variable) return;

            this.costVariables.push({
                CostId: variable.id,
                costVariable: { Name: variable.Name },
                Price: variable.Price || 0,
            });

            this.selectedCostVariable = null;
        },
        removeCostVariable(item) {
            const index = this.costVariables.findIndex(cv => cv.CostId === item.CostId);
            if (index > -1) {
                this.costVariables.splice(index, 1);
            }
        },
        loadInvoice(id) {
            const self = this;
            self.pageLoading = true;
            this.$store
                .dispatch("get", `/export-sales-invoice/${id}`)
                .then((res) => {
                    if (res.ResultCode == 1200) {
                        var invoice = res.ResponseData;
                        self.Invoice = invoice.Invoice;
                        self.orderItems = invoice.SaleRows || [];
                        self.costVariables = invoice.SaleCosts || [];
                        self.freightCost = self.Invoice.TotalFreightCost || 0;
                        self.advancePayment = self.Invoice.AdvancePayment || 0;
                        self.currencySymbol = self.Invoice.CurrencySymbol || "";
                        self.conversionRate = self.Invoice.ConversionRate || 1;

                        // populate editable header fields
                        self.record.CardCode = self.Invoice.CardCode;
                        self.record.Currency = self.Invoice.CurrencyId;
                        self.record.InvoiceType = self.Invoice.ExportSaleType;
                        self.record.AwbNo = self.Invoice.AwbNo;
                        self.date = self.Invoice.Date ? self.Invoice.Date.substr(0, 10) : new Date().toISOString().substr(0, 10);

                        // preload customer into the list so autocomplete shows it
                        if (self.Invoice.Customer) {
                            self.customers = [self.Invoice.Customer];
                        }
                    } else {
                        self.$refs.snackbar.show(res.ResultDesc, "red");
                    }
                })
                .catch((err) => {
                    self.$refs.snackbar.show(err, "red");
                })
                .finally(() => {
                    self.pageLoading = false;
                });
        },
        recalculateRow(item) {
            const qty = parseFloat(item.Quantity) || 0;
            const price = parseFloat(item.Price) || 0;
            item.LineTotal = (qty * price).toFixed(2);
        },
        saveChanges() {
            if (this.date == null || this.date == undefined) {
                this.$refs.snackbar.show("Please select date!", "red");
                return;
            }

            if (this.orderItems.length <= 0) {
                this.$refs.snackbar.show("You must have at least one product", "red");
                return;
            }

            const self = this;
            const invoiceId = this.$route.params.id;

            const rows = this.orderItems.map(row => ({
                id: row.id,
                Quantity: parseFloat(row.Quantity) || 0,
                Price: parseFloat(row.Price) || 0,
                TotalWeight: parseFloat(row.TotalWeight) || 0,
                LineTotal: parseFloat(row.LineTotal) || 0,
            }));

            const costs = this.costVariables.map(cost => ({
                CostId: cost.CostId,
                Price: parseFloat(cost.Price) || 0,
            }));

            const data = {
                invoiceId: parseInt(invoiceId),
                rows: rows,
                costs: costs,
                TotalFreightCost: parseFloat(this.freightCost) || 0,
                AdvancePayment: parseFloat(this.advancePayment) || 0,
                DocTotal: this.totalAmount,
                ConvertedTotal: this.convertedTotal,
                TotalWeight: parseFloat(this.totalWeight) || 0,
                CardCode: this.record.CardCode,
                CurrencyId: this.record.Currency,
                CurrencySymbol: this.currencySymbol,
                ConversionRate: this.conversionRate,
                ExportSaleType: this.record.InvoiceType,
                AwbNo: this.record.AwbNo,
                DocDate: this.date,
            };

            const url = "/update-export-sale";
            this.loader = true;
            this.$store
                .dispatch("put", { url, data })
                .then((res) => {
                    if (res.ResultCode == 1200) {
                        self.$refs.snackbar.show("Invoice updated successfully", "green");
                        setTimeout(() => {
                            self.$router.push(`/export-sale-invoices/${invoiceId}`);
                        }, 1000);
                    } else {
                        self.$refs.snackbar.show(res.ResultDesc, "red");
                    }
                })
                .catch((err) => {
                    self.$refs.snackbar.show(err, "red");
                })
                .finally(() => {
                    self.loader = false;
                });
        },
    },
    created() {
        Promise.all([
            this.getCurrencies(),
            this.getSaleTypes(),
            this.getAllCostVariables(),
        ]);
    },
};
</script>

<style scoped>
::v-deep input[type="number"]::-webkit-outer-spin-button,
::v-deep input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

::v-deep input[type="number"] {
    -moz-appearance: textfield;
}
</style>
